import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useSavedJob(jobId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();

  const queryKey = ["saved-job-status", userId, jobId];

  const { data: isSaved = false, isLoading } = useQuery({
    queryKey,
    enabled: !!userId && !!jobId,
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId || !jobId) return false;
      const { data, error } = await supabase
        .from("saved_jobs")
        .select("id")
        .eq("user_id", userId)
        .eq("job_id", jobId)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      if (!userId || !jobId) throw new Error("Not signed in");
      if (next) {
        const { error } = await supabase
          .from("saved_jobs")
          .insert({ user_id: userId, job_id: jobId });
        // ignore unique-violation (already saved)
        if (error && !String(error.message).toLowerCase().includes("duplicate")) {
          throw error;
        }
      } else {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", userId)
          .eq("job_id", jobId);
        if (error) throw error;
      }
      return next;
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<boolean>(queryKey);
      qc.setQueryData(queryKey, next);
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(queryKey, ctx.prev);
      toast.error("Could not update saved jobs");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["saved-jobs"] });
      qc.invalidateQueries({ queryKey: ["activity-counts"] });
    },
  });

  return {
    isSaved,
    isLoading,
    isPending: mutation.isPending,
    canSave: !!userId,
    toggle: () => mutation.mutate(!isSaved),
  };
}
